import type { InvoiceRunItemStatus } from '@clockwise/clients/invoices';
import { INVOICE_GENERATION_KIND_LABELS, sowScopeLabel } from '@clockwise/shared';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { formatMoney } from '@/components/custom/invoice-money';
import { PageHeader } from '@/components/layout/page-header';
import { PageShell } from '@/components/layout/page-shell';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { runStatusCopy, skipReasonCopy, useRun, useRunItems } from '@/lib/invoice-run-hooks';

/**
 * One generation run, in full.
 *
 * The exclusion counters matter as much as the inclusions here: "212 created"
 * out of 2,109 customers is alarming until "1,800 not due" is next to it. That
 * is why they are stored as counts on the run rather than as item rows —
 * `periodForRun` returns null on all thirty non-month-end days, so enumerating
 * them would be ~650k rows a year that nothing reads.
 */
export const Route = createFileRoute('/_authenticated/invoices/runs/$runId')({
  component: RunDetailPage,
});

function RunDetailPage() {
  const { runId } = Route.useParams();
  const [filter, setFilter] = useState<InvoiceRunItemStatus | 'attention' | 'all'>('attention');

  const run = useRun(runId);
  const items = useRunItems(runId, {
    limit: 100,
    offset: 0,
    // 'attention' has no single server status — the table's default view is
    // failures and skips, which the server already sorts first.
    ...(filter === 'all' || filter === 'attention' ? {} : { status: filter }),
  });

  if (run.isError) {
    return (
      <PageShell>
        <div className="rounded-lg border border-destructive/50 p-4" role="alert">
          <p className="text-destructive text-sm">
            {run.error instanceof Error ? run.error.message : "Couldn't load this run."}
          </p>
        </div>
      </PageShell>
    );
  }

  const r = run.data?.data;
  const status = r ? runStatusCopy(r.status) : null;
  const rows = (items.data?.data ?? []).filter((i) =>
    filter === 'attention' ? i.status === 'failed' || i.status === 'skipped' : true,
  );

  return (
    <PageShell>
      <PageHeader
        title={r ? monthLabel(r.invoiceMonth) : 'Generation run'}
        subtitle={
          r
            ? `${r.trigger === 'scheduled' ? 'Scheduled' : 'On demand'} · invoices dated ${formatDate(r.runDate)} · ${scopeLabel(r)}`
            : undefined
        }
      >
        {status ? (
          <Badge
            variant={
              status.tone === 'success'
                ? 'secondary'
                : status.tone === 'warning'
                  ? 'destructive'
                  : 'outline'
            }
          >
            {status.label}
          </Badge>
        ) : null}
        <Button asChild variant="outline">
          <Link to="/invoices/runs">All runs</Link>
        </Button>
      </PageHeader>

      {r ? (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
            <Stat label="Created" value={String(r.createdCount)} />
            {/* Beside Created, not among the exclusions below: a flagged
                invoice WAS raised. It is the one tile that leads somewhere —
                the list, already filtered — because unlike the others it names
                work a human still has to do. */}
            <Stat
              label="Flagged"
              value={String(r.flaggedCount ?? 0)}
              tone={(r.flaggedCount ?? 0) > 0 ? 'warn' : undefined}
              to={
                (r.flaggedCount ?? 0) > 0
                  ? {
                      to: '/invoices',
                      // Narrowed to THIS run by id — the same predicate the
                      // count uses. A date range could not do it: every run for
                      // a month shares its month-end date, so a follow-up run's
                      // "3" opened on every run's flagged invoices.
                      search: { flagged: 'true' as const, generationRunId: r.id },
                    }
                  : undefined
              }
            />
            <Stat label="Skipped" value={String(r.skippedCount)} />
            <Stat
              label="Failed"
              value={String(r.failedCount)}
              tone={r.failedCount > 0 ? 'bad' : undefined}
            />
            {/* "Unknown", not a dash: `formatMoney` returns null only when it
                cannot honestly render the value, and a dash there would read
                as a real figure (G15). */}
            <Stat label="Total invoiced" value={formatMoney(r.createdTotal) ?? 'Unknown'} />
          </div>

          {/* Ruled out BEFORE enumeration, so they have no item rows. Shown
              because they are what makes the created count make sense. */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label="Not due" value={String(r.notDueCount)} muted />
            <Stat label="Paused" value={String(r.pausedCount)} muted />
            <Stat label="Filtered out" value={String(r.filteredCount)} muted />
            <Stat
              label="Considered"
              value={String(r.totalItems + r.notDueCount + r.pausedCount + r.filteredCount)}
              muted
            />
          </div>
        </>
      ) : null}

      <div className="flex flex-wrap items-center gap-3">
        {/* SOWs, not customers: the run's unit of work is the agreement, and
            a customer holding two appears twice by design. */}
        <h2 className="font-medium text-sm">Engagements</h2>
        <select
          aria-label="Filter engagements"
          className="h-8 rounded-md border border-input bg-transparent px-2 text-sm"
          onChange={(e) => setFilter(e.target.value as typeof filter)}
          value={filter}
        >
          <option value="attention">Needs attention</option>
          <option value="all">All</option>
          <option value="created">Created</option>
          <option value="skipped">Skipped</option>
          <option value="failed">Failed</option>
        </select>
      </div>

      {items.isError ? (
        <div className="rounded-lg border border-destructive/50 p-4" role="alert">
          <p className="text-destructive text-sm">
            {items.error instanceof Error ? items.error.message : "Couldn't load this run's items."}
          </p>
        </div>
      ) : items.isPending ? (
        <p className="text-muted-foreground text-sm">Loading…</p>
      ) : rows.length === 0 ? (
        <div className="rounded-lg border p-6 text-center">
          <p className="text-muted-foreground text-sm">
            {filter === 'attention'
              ? 'Nothing needs attention in this run.'
              : 'No engagements match this filter.'}
          </p>
        </div>
      ) : (
        <ul className="divide-y rounded-lg border">
          {rows.map((item) => {
            const copy = item.skipReason ? skipReasonCopy(item.skipReason) : null;
            return (
              <li key={item.id} className="flex items-center gap-3 px-3 py-2.5">
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium text-sm">
                    {item.customerName}
                    {/* Only when the document names ONE agreement. A gathered
                        invoice covers several, and naming one of them here
                        would attribute the whole row to it. */}
                    {item.sowId === null ? null : (
                      <span className="ml-1.5 font-normal text-muted-foreground">
                        {item.sowName}
                      </span>
                    )}
                    {/* Says why this row's fees and hours share one document
                        — otherwise a reader seeing hours on a retainer invoice
                        has no way to tell it was deliberate. */}
                    {item.consolidated ? (
                      <span className="ml-1.5 font-normal text-muted-foreground">
                        · consolidated
                      </span>
                    ) : null}
                  </p>
                  <p
                    className={`text-xs ${
                      item.status === 'failed' ? 'text-destructive' : 'text-muted-foreground'
                    }`}
                  >
                    {item.status === 'created'
                      ? 'Invoice created.'
                      : item.status === 'failed'
                        ? (item.lastError ??
                          "Couldn't be invoiced due to a system error — support has been notified.")
                        : (copy?.label ?? 'Skipped.')}
                  </p>
                </div>
                {item.invoiceId ? (
                  <Button asChild size="sm" variant="outline">
                    <Link params={{ id: item.invoiceId }} to="/invoices/$id">
                      Open invoice
                    </Link>
                  </Button>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </PageShell>
  );
}

/**
 * One number from the run.
 *
 * `to` makes the tile a link. Only the Flagged tile uses it, and only when
 * there is something to look at: a tile that navigates to an empty list is
 * worse than one that does not invite the click.
 */
function Stat({
  label,
  value,
  muted,
  tone,
  to,
}: {
  label: string;
  value: string;
  muted?: boolean;
  tone?: 'bad' | 'warn';
  to?: {
    to: '/invoices';
    search: { flagged: 'true'; generationRunId: string };
  };
}) {
  const body = (
    <>
      <p className="text-muted-foreground text-xs">{label}</p>
      <p
        className={`font-medium ${muted ? 'text-lg text-muted-foreground' : 'text-2xl'} ${
          tone === 'bad'
            ? 'text-destructive'
            : tone === 'warn'
              ? 'text-amber-600 dark:text-amber-400'
              : ''
        }`}
      >
        {value}
      </p>
    </>
  );
  if (to === undefined) {
    return <div className="rounded-lg bg-muted/40 px-3 py-2.5">{body}</div>;
  }
  return (
    <Link
      to={to.to}
      search={to.search}
      className="rounded-lg bg-muted/40 px-3 py-2.5 transition-colors hover:bg-muted"
    >
      {body}
    </Link>
  );
}

/** See the identical note on the run list: a T&M run's empty filter columns
 *  are not "every engagement", they are "this engine has no filters". */
function scopeLabel(run: {
  runKind: 'retainer' | 'tm';
  customerTypes: string[];
  invoiceCadences: string[];
}): string {
  if (run.runKind === 'tm') return INVOICE_GENERATION_KIND_LABELS.tm;
  return sowScopeLabel({ lineTypes: run.customerTypes, frequencies: run.invoiceCadences });
}

function monthLabel(iso: string): string {
  return new Date(`${iso}T00:00:00Z`).toLocaleString('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

function formatDate(iso: string): string {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-US', { timeZone: 'UTC' });
}
